// mapCollaborationPlugins.js — EditorState factory for the headless map editor.
//
// The map editor has no EditorView — PM state is managed as plain React state.
// ySyncPlugin requires a live EditorView and cannot run headless, so we keep
// prosemirror-history for undo/redo and use fragment.observe in MapEditorPortal
// to push incoming remote Yjs changes back into the React EditorState.
//
// awareness is accepted for forward-compatibility (cursor plugin, presence) but
// is not wired here until a view is available.

import {EditorState} from 'prosemirror-state';
import {history, undo, redo} from 'prosemirror-history';
import {keymap} from 'prosemirror-keymap';
import {mapSchema, createEmptyMapDocJSON} from '../schema/mapSchema';
import {mapDocFromFragment} from '../schema/mapPmAdapter';

/**
 * Create a headless EditorState for a single map editing session.
 * The initial doc is read from the Y.XmlFragment (if non-empty), or an empty
 * map doc is used for new nodes.
 *
 * @param {import('yjs').XmlFragment} fragment    the report's shared map fragment (see
 *   mapFragmentInit.js's getSharedMapFragment) — shared by every map node embed in the report.
 * @param {import('y-protocols/awareness').Awareness} [_awareness]  reserved for future cursor plugin
 * @returns {import('prosemirror-state').EditorState}
 */
export function mapStateFromFragment(fragment, _awareness) {
    const existingDoc = mapDocFromFragment(fragment);
    const doc = existingDoc ?? mapSchema.nodeFromJSON(createEmptyMapDocJSON());
    return EditorState.create({
        schema: mapSchema,
        doc,
        plugins: [
            history(),
            keymap({'Mod-z': undo, 'Mod-y': redo, 'Mod-Shift-z': redo}),
        ],
    });
}
